import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { partnerApiService } from '../services/api';
import { Tag } from '../types/api';

const TAG_LIMIT = 20;
const TAG_MAX_LENGTH = 40;

export interface TagsEditorHandle {
  /** True если набор тегов отличается от изначально загруженного */
  isDirty: () => boolean;
  /** Sync с сервером. Возвращает обновлённый список. */
  saveIfDirty: () => Promise<{ saved: boolean; error?: string }>;
}

interface TagsEditorProps {
  initialTags?: Tag[];
  disabled?: boolean;
}

const normalizeKey = (name: string) => name.trim().toLowerCase().replace(/\s+/g, ' ');

export const TagsEditor = forwardRef<TagsEditorHandle, TagsEditorProps>(
  ({ initialTags, disabled }, ref) => {
    const [tags, setTags] = useState<Tag[]>(initialTags ?? []);
    const [initialKeySet, setInitialKeySet] = useState<Set<string>>(
      () => new Set((initialTags ?? []).map((t) => normalizeKey(t.name)))
    );
    const [query, setQuery] = useState('');
    const [suggestions, setSuggestions] = useState<Tag[]>([]);
    const [isSuggesting, setIsSuggesting] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const initializedRef = useRef(false);

    // Инициализация только один раз — чтобы фоновый рефреш partner не затирал
    useEffect(() => {
      if (initializedRef.current) return;
      if (initialTags) {
        initializedRef.current = true;
        setTags(initialTags);
        setInitialKeySet(new Set(initialTags.map((t) => normalizeKey(t.name))));
      }
    }, [initialTags]);

    // Debounced suggest
    useEffect(() => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      const trimmed = query.trim();
      if (trimmed.length === 0) {
        setSuggestions([]);
        setIsSuggesting(false);
        return;
      }
      setIsSuggesting(true);
      debounceRef.current = setTimeout(async () => {
        try {
          const result = await partnerApiService.suggestTags(trimmed);
          setSuggestions(result);
        } catch (e) {
          setSuggestions([]);
        } finally {
          setIsSuggesting(false);
        }
      }, 250);
      return () => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
      };
    }, [query]);

    // Закрытие подсказок при клике вне
    useEffect(() => {
      const handler = (e: MouseEvent) => {
        if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
          setIsFocused(false);
        }
      };
      document.addEventListener('mousedown', handler);
      return () => document.removeEventListener('mousedown', handler);
    }, []);

    const hasTag = (name: string) => tags.some((t) => normalizeKey(t.name) === normalizeKey(name));

    const addTag = (raw: string) => {
      const trimmed = raw.trim();
      if (trimmed.length < 2) return;
      if (trimmed.length > TAG_MAX_LENGTH) return;
      if (tags.length >= TAG_LIMIT) return;
      if (hasTag(trimmed)) return;
      // временный id (отрицательный) до sync
      const tempId = -(Date.now() + Math.floor(Math.random() * 1000));
      setTags((prev) => [...prev, { id: tempId, name: trimmed, slug: normalizeKey(trimmed) }]);
      setQuery('');
      setSuggestions([]);
    };

    const addExistingTag = (tag: Tag) => {
      if (tags.length >= TAG_LIMIT) return;
      if (hasTag(tag.name)) return;
      setTags((prev) => [...prev, tag]);
      setQuery('');
      setSuggestions([]);
    };

    const removeTag = (id: number) => {
      setTags((prev) => prev.filter((t) => t.id !== id));
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        if (query.trim().length > 0) addTag(query);
      } else if (e.key === 'Backspace' && query.length === 0 && tags.length > 0) {
        e.preventDefault();
        removeTag(tags[tags.length - 1].id);
      }
    };

    useImperativeHandle(
      ref,
      () => ({
        isDirty: () => {
          const currentKeys = new Set(tags.map((t) => normalizeKey(t.name)));
          if (currentKeys.size !== initialKeySet.size) return true;
          for (const k of Array.from(currentKeys)) if (!initialKeySet.has(k)) return true;
          return false;
        },
        saveIfDirty: async () => {
          const currentKeys = new Set(tags.map((t) => normalizeKey(t.name)));
          let dirty = currentKeys.size !== initialKeySet.size;
          if (!dirty) {
            for (const k of Array.from(currentKeys)) if (!initialKeySet.has(k)) { dirty = true; break; }
          }
          if (!dirty) return { saved: false };
          try {
            const result = await partnerApiService.syncPartnerTags(tags.map((t) => t.name));
            setTags(result);
            setInitialKeySet(new Set(result.map((t) => normalizeKey(t.name))));
            return { saved: true };
          } catch (e: any) {
            const msg =
              e.response?.data?.message ||
              (e.response?.data?.errors ? Object.values(e.response.data.errors).flat()[0] : null) ||
              e.message ||
              'Не удалось сохранить теги';
            return { saved: false, error: String(msg) };
          }
        },
      }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [tags, initialKeySet]
    );

    const filteredSuggestions = suggestions.filter((s) => !hasTag(s.name));
    const showCreateOption =
      query.trim().length >= 2 &&
      !filteredSuggestions.some((s) => normalizeKey(s.name) === normalizeKey(query)) &&
      !hasTag(query) &&
      tags.length < TAG_LIMIT;

    return (
      <div ref={wrapperRef}>
        {/* Выбранные теги */}
        {tags.length > 0 && (
          <div className="qp-chips" style={{ marginBottom: 12 }}>
            {tags.map((tag) => (
              <span key={tag.id} className="qp-chip">
                {tag.name}
                <button
                  type="button"
                  className="qp-chip-x"
                  onClick={() => removeTag(tag.id)}
                  disabled={disabled}
                  aria-label={`Убрать тег ${tag.name}`}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                    <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
                  </svg>
                </button>
              </span>
            ))}
          </div>
        )}

        {/* Input */}
        <div style={{ position: 'relative' }}>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value.slice(0, TAG_MAX_LENGTH))}
            onFocus={() => setIsFocused(true)}
            onKeyDown={handleKeyDown}
            placeholder={
              tags.length >= TAG_LIMIT
                ? `Достигнут лимит ${TAG_LIMIT} тегов`
                : 'Например: Хэллоуин, Живая музыка, Веганское'
            }
            disabled={disabled || tags.length >= TAG_LIMIT}
            maxLength={TAG_MAX_LENGTH}
            className="qp-control"
          />

          {/* Suggestions dropdown */}
          {isFocused && (filteredSuggestions.length > 0 || showCreateOption || isSuggesting) && (
            <div className="qp-suggest">
              {isSuggesting && filteredSuggestions.length === 0 && !showCreateOption && (
                <p className="qp-tree-empty">Ищем…</p>
              )}

              {filteredSuggestions.map((tag) => (
                <button
                  key={tag.id}
                  type="button"
                  className="qp-suggest-item"
                  onClick={() => addExistingTag(tag)}
                >
                  <span style={{ flex: 1, minWidth: 0 }}>
                    {tag.is_curated && (
                      <span className="qp-suggest-star" aria-label="Из библиотеки">
                        ★
                      </span>
                    )}
                    {tag.name}
                  </span>
                  {tag.usage_count !== undefined && tag.usage_count > 0 && (
                    <span className="qp-mono qp-faint" style={{ fontSize: 11 }}>
                      {tag.usage_count}
                    </span>
                  )}
                </button>
              ))}

              {showCreateOption && (
                <button
                  type="button"
                  className="qp-suggest-item qp-suggest-item--new"
                  onClick={() => addTag(query)}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                    <path d="M12 5v14M5 12h14" strokeLinecap="round" />
                  </svg>
                  <span>Создать тег «{query.trim()}»</span>
                </button>
              )}
            </div>
          )}
        </div>

        <p className="qp-field-hint" style={{ margin: '8px 0 0' }}>
          {tags.length}/{TAG_LIMIT} · до {TAG_MAX_LENGTH} символов · Enter — добавить тег
        </p>
      </div>
    );
  }
);

TagsEditor.displayName = 'TagsEditor';
